/**
 * Pure selection maths for the Files & Media grid (web).
 *
 * Kept free of React and of Document/LocalFile shapes so the Shift/Ctrl rules can
 * be unit-tested directly — the grid itself only translates items to keys and
 * renders the result.
 *
 * The hover/Shift state behind the range *preview* lives in
 * `@hooks/useRangeHoverState`, since it needs React state and event wiring.
 */

/** Maximum number of files that can be selected at once in Files & Media. */
export const MAX_SELECTABLE_FILES = 25;

export type SelectionUpdate = {
  /** Selected keys after the click, or null to keep the current selection. */
  keys: Set<string> | null;
  /** Anchor after the click. Shift-click deliberately keeps the existing anchor. */
  anchorIndex: number | null;
  /** True when the click was rejected for exceeding `maxSelectable`. */
  limitExceeded: boolean;
};

export type SelectionInput = {
  /** Keys of every grid item, in rendered order. */
  itemKeys: string[];
  selectedKeys: ReadonlySet<string>;
  /** Key of the clicked item — taken from the item, not `itemKeys[clickedIndex]`. */
  clickedKey: string;
  clickedIndex: number;
  anchorIndex: number | null;
  shiftKey: boolean;
  ctrlOrCmdKey: boolean;
  /** True when a plain click should toggle rather than replace the selection. */
  inSelectMode: boolean;
  maxSelectable?: number;
};

/**
 * Resolve a click into the next selection. Side-effect free: the caller applies
 * `keys`/`anchorIndex` and surfaces `limitExceeded`.
 */
export const computeSelectionUpdate = ({
  itemKeys,
  selectedKeys,
  clickedKey,
  clickedIndex,
  anchorIndex,
  shiftKey,
  ctrlOrCmdKey,
  inSelectMode,
  maxSelectable = MAX_SELECTABLE_FILES,
}: SelectionInput): SelectionUpdate => {
  const keys = new Set(selectedKeys);

  // Shift: range select, resetting the selection to anchor->clicked.
  if (shiftKey && anchorIndex !== null) {
    const start = Math.min(anchorIndex, clickedIndex);
    const end = Math.max(anchorIndex, clickedIndex);

    // Clamped to the list so a stale anchor — one set before a filter shrank the
    // grid — cannot walk indices that no longer exist.
    const from = Math.max(0, start);
    const to = Math.min(itemKeys.length - 1, end);

    const rangeKeys = new Set<string>();
    for (let k = from; k <= to; k++) {
      const key = itemKeys[k];
      if (key === undefined) continue;
      rangeKeys.add(key);
    }

    // Cap against the keys actually selected rather than the raw index span:
    // a stale anchor can span positions with no item, and those must not count
    // toward the limit or a legitimate selection gets rejected. The range branch
    // used to add with no bound at all, unlike Ctrl and plain click (TA-10163).
    if (rangeKeys.size > maxSelectable) {
      return { keys: null, anchorIndex, limitExceeded: true };
    }

    // Shift replaces the selection outright, so the range is the whole result.
    return { keys: rangeKeys, anchorIndex, limitExceeded: false };
  }

  // Ctrl/Cmd: toggle a single item.
  if (ctrlOrCmdKey) {
    if (keys.has(clickedKey)) keys.delete(clickedKey);
    else if (keys.size < maxSelectable) keys.add(clickedKey);
    else return { keys: null, anchorIndex, limitExceeded: true };

    return { keys, anchorIndex: clickedIndex, limitExceeded: false };
  }

  // Plain click: toggle while selecting, otherwise replace the selection.
  if (inSelectMode) {
    if (keys.has(clickedKey)) keys.delete(clickedKey);
    else if (keys.size < maxSelectable) keys.add(clickedKey);
    else return { keys: null, anchorIndex, limitExceeded: true };
  } else {
    keys.clear();
    keys.add(clickedKey);
  }

  return { keys, anchorIndex: clickedIndex, limitExceeded: false };
};
